import { Box, IconChevron } from 'braid-design-system';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AddFilterButton } from './AddFilterButton.tsx';
import { FilterDisplay } from './FilterDisplay.tsx';
import { countFittingFilters } from './countFittingFilters.ts';
import { isRequiredFilter } from './filters.ts';
import type { FilterData } from './types.ts';

import { LightTag } from '#src/components/LightTag/LightTag.tsx';

const ExpandToggle = ({
  expanded,
  hiddenCount,
  onClick,
}: {
  expanded: boolean;
  hiddenCount: number;
  onClick: () => void;
}) => (
  <LightTag
    tone="secondary"
    onClick={onClick}
    expanded={expanded}
    icon={<IconChevron direction={expanded ? 'up' : 'down'} />}
    iconPosition="trailing"
  >
    {expanded ? 'Show less' : `+${hiddenCount} more`}
  </LightTag>
);

const noop = () => undefined;

type Measurement = {
  filters: FilterData[];
  fittingCount: number;
};

/**
 * Measures how many of the most recent filters to show when collapsed, given
 * the leading content and the expand toggle.
 */
const useFittingFilterCount = (filters: FilterData[]) => {
  const rowRef = useRef<HTMLElement>(null);
  const leadingRef = useRef<HTMLElement>(null);
  const filtersMeasureRef = useRef<HTMLElement>(null);
  const toggleMeasureRef = useRef<HTMLElement>(null);
  const [measurement, setMeasurement] = useState<Measurement>();

  useLayoutEffect(() => {
    const row = rowRef.current;
    const leading = leadingRef.current;
    const filtersMeasure = filtersMeasureRef.current;
    const toggleMeasure = toggleMeasureRef.current;

    if (!row || !leading || !filtersMeasure || !toggleMeasure) {
      return;
    }

    const recalculate = () => {
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;

      const fittingCount = countFittingFilters({
        rowWidth: Math.floor(row.getBoundingClientRect().width),
        leadingWidth: Math.ceil(leading.getBoundingClientRect().width),
        filterWidths: Array.from(filtersMeasure.children, (element) =>
          Math.ceil(element.getBoundingClientRect().width),
        ),
        toggleWidth: Math.ceil(toggleMeasure.getBoundingClientRect().width),
        gap,
      });

      setMeasurement((current) =>
        current?.filters === filters && current.fittingCount === fittingCount
          ? current
          : { filters, fittingCount },
      );
    };

    recalculate();

    // The measuring layers resize when filters change or fonts load.
    const observer = new ResizeObserver(recalculate);
    observer.observe(row);
    observer.observe(leading);
    observer.observe(filtersMeasure);
    observer.observe(toggleMeasure);

    return () => observer.disconnect();
  }, [filters]);

  return {
    rowRef,
    leadingRef,
    filtersMeasureRef,
    toggleMeasureRef,
    fittingCount: measurement?.fittingCount,
    // The first render after the filters change still sees the previous
    // measurement; the layout effect corrects it before paint.
    isMeasurementCurrent: measurement?.filters === filters,
  };
};

export const ActiveFilters = ({
  filters,
  onAddFilter,
  onEditFilter,
  onRemoveFilter,
}: {
  filters: FilterData[];
  onAddFilter: () => void;
  onEditFilter: (filter: FilterData) => void;
  onRemoveFilter: (filter: FilterData) => void;
}) => {
  // Required filters explain why the data is narrowed, so they're always shown
  // and only the user's filters collapse.
  const { requiredFilters, userFilters } = useMemo(
    () => ({
      requiredFilters: filters.filter(isRequiredFilter),
      userFilters: filters.filter((filter) => !isRequiredFilter(filter)),
    }),
    [filters],
  );

  const [expanded, setExpanded] = useState(false);
  const {
    rowRef,
    leadingRef,
    filtersMeasureRef,
    toggleMeasureRef,
    fittingCount,
    isMeasurementCurrent,
  } = useFittingFilterCount(userFilters);

  // Show every filter until the first measurement has happened.
  const hiddenCount = Math.max(
    userFilters.length - (fittingCount ?? userFilters.length),
    0,
  );
  const canCollapse = hiddenCount > 0;

  // Reset during render rather than in an effect so the next time the filters
  // overflow the line they start collapsed again. Skip outdated measurements
  // so removing a filter doesn't collapse the list before it's re-measured.
  if (isMeasurementCurrent && !canCollapse && expanded) {
    setExpanded(false);
  }

  const visibleUserFilters =
    canCollapse && !expanded ? userFilters.slice(hiddenCount) : userFilters;

  const renderFilter = (filter: FilterData) => (
    <FilterDisplay
      filter={filter}
      key={filter.id}
      edit={() => onEditFilter(filter)}
      remove={() => onRemoveFilter(filter)}
    />
  );

  return (
    <Box
      ref={rowRef}
      position="relative"
      display="flex"
      flexWrap="wrap"
      gap="small"
      style={{ alignItems: 'stretch' }}
    >
      <Box
        ref={leadingRef}
        display="flex"
        flexWrap="wrap"
        gap="small"
        style={{ alignItems: 'stretch' }}
      >
        <AddFilterButton onClick={onAddFilter} />

        {filters.length > 0 ? (
          <Box
            style={{ width: 1 }}
            boxShadow="borderNeutralLight"
            marginY="xxsmall"
          />
        ) : null}

        {requiredFilters.map(renderFilter)}
      </Box>

      {visibleUserFilters.map(renderFilter)}

      {canCollapse ? (
        <ExpandToggle
          expanded={expanded}
          hiddenCount={hiddenCount}
          onClick={() => setExpanded((current) => !current)}
        />
      ) : null}

      {/*
        Hidden copies of the user's filters and the expand toggle, used to
        measure their natural widths. The zero-size clipping wrapper stops them
        from causing page overflow.
      */}
      <Box
        aria-hidden
        inert
        position="absolute"
        overflow="hidden"
        pointerEvents="none"
        top={0}
        left={0}
        style={{ width: 0, height: 0, visibility: 'hidden' }}
      >
        <Box
          ref={filtersMeasureRef}
          display="flex"
          gap="small"
          style={{ width: 'max-content' }}
        >
          {userFilters.map((filter) => (
            <FilterDisplay
              filter={filter}
              key={filter.id}
              edit={noop}
              remove={noop}
            />
          ))}
        </Box>

        <Box
          ref={toggleMeasureRef}
          display="flex"
          style={{ width: 'max-content' }}
        >
          {/* Worst case label, so the reserved width covers any hidden count. */}
          <ExpandToggle
            expanded={false}
            hiddenCount={userFilters.length}
            onClick={noop}
          />
        </Box>
      </Box>
    </Box>
  );
};
